// client/src/components/CommentsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

/**
 * CommentsPage
 * Modale/page listant tous les commentaires d'un message public.
 *
 * Props :
 *   messageId     — identifiant du message
 *   currentUserId — identifiant de l'utilisateur connecté
 *   isCreator     — true si l'utilisateur est l'éditeur de la diffusion
 *   onClose       — callback fermeture
 *   onCountChange — callback(delta) pour mettre à jour le compteur parent
 */
function CommentsPage({ messageId, currentUserId, isCreator, onClose, onCountChange, socket }) {
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const [comments, setComments]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(false);
  const [banConfirm, setBanConfirm] = useState(null); // userId en attente de confirmation
const [banSuccess, setBanSuccess] = useState(null);
const [editingCommentId, setEditingCommentId] = useState(null);
const [editingText, setEditingText] = useState('');

  const PER_PAGE = 20;

  /* ── Charger les commentaires ── */
  const fetchComments = useCallback(async (pageNum = 1, reset = false) => {
    const token = localStorage.getItem('token');
    try {
      const res = await api.get(
        `${API_URL}/api/comments/${messageId}?page=${pageNum}&limit=${PER_PAGE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data.comments || [];
      setComments(prev => reset ? data : [...prev, ...data]);
      setHasMore(res.data.hasMore || false);
    } catch (err) {
      console.error('Erreur chargement commentaires', err);
    } finally {
      setLoading(false);
    }
  }, [messageId, API_URL]);

  useEffect(() => { fetchComments(1, true); }, [fetchComments]);

  /* ── Charger plus ── */
  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchComments(next);
  };

useEffect(() => {
if (!socket) return;
  socket.on('commentUpdated', (updatedComment) => {
    setComments(prev =>
      prev.map(c =>
        c._id === updatedComment._id ? updatedComment : c
      )
    );
  });

  return () => socket.off('commentUpdated');
}, []);

  /* ── Supprimer un commentaire (éditeur uniquement) ── */
  const handleDelete = async (commentId) => {
    const token = localStorage.getItem('token');
    try {
      await api.delete(
        `${API_URL}/api/comments/${messageId}/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(prev => prev.filter(c => c._id !== commentId));
      onCountChange(-1);
    } catch (err) {
      console.error('Erreur suppression commentaire', err);
    }
  };

 /* ── modifier un commentaire ── */
const handleUpdate = async (commentId) => {
  const token = localStorage.getItem('token');

  try {
    const res = await api.put(
      `${API_URL}/api/comments/${messageId}/${commentId}`,
      { text: editingText },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const updated = res.data;

    setComments(prev =>
      prev.map(c => c._id === commentId ? updated : c)
    );

    setEditingCommentId(null);

  } catch (err) {
    console.error('Erreur update commentaire', err);
  }
};

const navigate = useNavigate();

const navigateToProfile = (userId) => {
  navigate(`/profile/${userId}?from=comments`);
};

  /* ── Bannir un utilisateur (éditeur uniquement) ── */
  const handleBan = async (userId) => {
  const token = localStorage.getItem('token');
  try {
    await api.post(
      `${API_URL}/api/comments/ban`,
      { userId, messageId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // ← ne pas toucher aux commentaires ni au compteur
    // Juste afficher un message de succès
    const bannedUser = comments.find(c => c.author?._id === userId);
    setBanSuccess(bannedUser?.author?.username || t('diffusion.comments_unknownUser'));
    setBanConfirm(null);

  } catch (err) {
        setBanConfirm(null);
  }
};

  /* ── Formater la date ── */
  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div  style={styles.panel} onClick={e => e.stopPropagation()}>

        {/* ── En-tête ── */}
        <div style={styles.header}>
          <span style={styles.title}>
            {t('diffusion.comments_title', 'Commentaires')}
          </span>
          <button onClick={onClose} style={styles.closeBtn} title={t('diffusion.comments_close', 'Fermer')}>✕</button>
        </div>

        {/* ── Liste ── */}
        <div style={styles.list}>
          {loading && (
            <p style={styles.hint}>{t('diffusion.comments_loading', 'Chargement…')}</p>
          )}

          {!loading && comments.length === 0 && (
            <p style={styles.hint}>{t('diffusion.comments_empty', 'Aucun commentaire pour l\'instant.')}</p>
          )}

          {comments.map(comment => (
            <div key={comment._id} style={styles.item}>

              {/* ── Avatar ── */}
              <img
                src={
                  comment.author?.profileImage
                    ? `${API_URL}/api/decryptedFile/decrypted-cloudinary-file/${comment.author._id}?token=${localStorage.getItem('token')}&type=image&thumb=true&profile=true`
                    : `${API_URL}/uploads/default-profile.png`
                }
                alt="avatar"
 		onClick={e => {
                    e.stopPropagation();
                    navigateToProfile(comment.author._id);
                  }}

                style={{ ...styles.avatar, cursor: 'pointer' }}
              />

              {/* ── Contenu ── */}
              <div style={styles.body}>
                <div style={styles.meta}>
                  <span style={styles.username}>
                    {comment.author?.username || t('diffusion.comments_unknownUser', 'Utilisateur')}
                  </span>
                  <span style={styles.date}>{formatDate(comment.createdAt)}</span>
                </div>
                {editingCommentId === comment._id ? (
  <>
    <textarea
      value={editingText}
      onChange={(e) => setEditingText(e.target.value)}
      style={styles.textarea}
    />
    <div style={{ display: 'flex', gap: 6 }}>
      <button style={{ background: 'none' }} onClick={() => handleUpdate(comment._id)}>💾</button>
      <button style={{ background: 'none' }}  onClick={() => setEditingCommentId(null)}>❌</button>
    </div>
  </>
) : (
  <>
<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={styles.text}>{comment.text}</span>

    <span style={styles.date}>
      
      {comment.updatedAt !== comment.createdAt && (
        <> ({t('discussion.update', 'modifié')})</>
      )}
    </span></div>
  </>

)}
              </div>
	{/* ── modifications ── */}
	{comment.author?._id === currentUserId && (
  	<button
   	 onClick={() => {
      	setEditingCommentId(comment._id);
      	setEditingText(comment.text);
    	}}
    	style={styles.actionBtn}
    	title="Modifier"
  	>
    	✏️
  	</button>
	)}
	
              {/* ── Actions  ── */}
              {(isCreator || comment.author?._id === currentUserId) && (
  <div style={styles.actions}>

    {/* ── Supprimer (auteur OU créateur) ── */}
    <button
      onClick={() => handleDelete(comment._id)}
      style={styles.actionBtn}
      title={t('diffusion.comments_delete', 'Supprimer ce commentaire')}
    >
      ✕
    </button>

    {/* ── Bannir (créateur UNIQUEMENT, pas lui-même) ── */}
    {isCreator && comment.author?._id !== currentUserId && (
      <button
        onClick={() => setBanConfirm(comment.author?._id)}
        style={{ ...styles.actionBtn, color: 'red' }}
        title={t('diffusion.comments_ban', 'Bannir cet utilisateur')}
      >
        🚫
      </button>
    )}

  </div>
)}
            </div>
          ))}

          {/* ── Pagination ── */}
          {hasMore && (
            <button onClick={loadMore} style={styles.loadMore}>
              {t('diffusion.comments_loadMore', 'Charger plus')}
            </button>
          )}
        </div>

        {/* ── Modale confirmation bannissement ── */}
        {banConfirm && (
          <div style={styles.confirmOverlay} onClick={e => e.stopPropagation()}>
            <div style={styles.confirmBox} onClick={e => e.stopPropagation()}>
              <p style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>
                {t('diffusion.comments_confirmBan', 'Bannir cet utilisateur ? Il ne pourra plus commenter vos éditions.')}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setBanConfirm(null)} style={styles.cancelBtn}>
                  {t('diffusion.comments_cancel', 'Annuler')}
                </button>
                <button onClick={() => handleBan(banConfirm)} style={styles.banBtn}>
                  {t('diffusion.comments_confirmBanBtn', 'Bannir')}
                </button>
              </div>
            </div>
          </div>
        )}
{/* ── Message succès bannissement ── */}
{banSuccess && (
  <div
    style={styles.confirmOverlay}
    onClick={e => e.stopPropagation()}
  >
    <div style={styles.confirmBox} onClick={e => e.stopPropagation()}>
      <p style={{ margin: '0 0 12px', fontSize: '0.9rem', textAlign: 'center' }}>
        🚫 {t('diffusion.comments_banSuccess', 
          `${banSuccess} a été banni. Il ne pourra plus commenter vos éditions.`,
          { username: banSuccess }
        )}
      </p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button 
          onClick={() => setBanSuccess(null)} 
          style={styles.cancelBtn}
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}

      </div>
    </div>
  );
}

/* ── Styles ── */
const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.45)',
display: 'flex', alignItems: 'center', justifyContent: 'center',

      },
  panel: {
    background: 'var(--card-bg)',
    borderRadius: '16px 16px 0 0',
    width: '100%', maxWidth: '640px',
    maxHeight: '75vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid var(--color-border-tertiary)',
    flexShrink: 0,
  },
  title: {
    fontWeight: 500, fontSize: '1rem',
    color: 'var(--color-text-primary)',
  },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '16px', color: 'var(--color-text-secondary)',
  },
  list: {
    overflowY: 'auto', flex: 1, padding: '8px 16px 16px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  hint: {
    textAlign: 'center', color: 'var(--color-text-secondary)',
    fontSize: '0.85rem', margin: '20px 0',
  },
  item: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '8px',
    borderRadius: '10px',
    background: 'var(--color-background-secondary)',
  },
  avatar: {
    width: '34px', height: '34px', borderRadius: '50%',
    objectFit: 'cover', flexShrink: 0,
  },
  body: {
    flex: 1, minWidth: 0,
  },
  meta: {
    display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '3px',
  },
  username: {
    fontWeight: 500, fontSize: '0.85rem',
    color: 'var(--discussion-creator)',
  },
  date: {
    fontSize: '0.65rem', color: 'var(--date_message-color)',
  },
  text: {
    margin: 0, fontSize: '0.88rem', color: 'var(--color-text-primary)',
    wordBreak: 'break-word', lineHeight: 1.45,
  },
  actions: {
    display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0,
  },
  actionBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: 'red',
    padding: '2px',
  },
  loadMore: {
    background: 'none', border: '1px solid var(--color-border-secondary)',
    borderRadius: '20px', padding: '6px 16px',
    cursor: 'pointer', fontSize: '0.82rem',
    color: '#007bff', margin: '8px auto 0',
    display: 'block',
  },
  confirmOverlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '16px 16px 0 0',
  },
  confirmBox: {
    background: '#fff',
    borderRadius: '12px', padding: '20px',
    width: '88%', maxWidth: '340px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  cancelBtn: {
    background: 'var(--color-background-secondary)',
    border: '1px solid var(--color-border-secondary)',
    borderRadius: '8px', padding: '6px 14px',
    cursor: 'pointer', fontSize: '0.85rem',
    color: 'var(--color-text-primary)',
  },
  banBtn: {
    background: 'red', border: 'none',
    borderRadius: '8px', padding: '6px 14px',
    cursor: 'pointer', fontSize: '0.85rem', color: '#fff',
  },
};

export default CommentsPage;
